import { ReactNode, Children, isValidElement, cloneElement } from "react";
import BulletedTextListDescription from "./BullettedTextListDescription";
import { BulletedTextListItemProps } from "./BulletedTextListItem";
import { cn } from "@/lib/utils";
import DegreeImageRightWrapper from "@/components/DegreeDetail/DegreeDetailLayout/components/DegreeImageRight/DegreeImageRightWrapper";

export interface BulletedTextListProps {
  children: ReactNode;
  header: string;
  description?: string | ReactNode;
  bodyClassName?: string;
  headerClassName?: string;
  variant?: "default" | "small";
  icon?: "bullet-point" | "check" | "check-circle" | "none";
  showHeaderColon?: boolean;
  columns?: number;
  uuid?: string;
  isFlexDetailpage?: boolean;
}

export default function BulletedTextList({
  children,
  header,
  description,
  bodyClassName,
  headerClassName,
  variant = "default",
  icon,
  showHeaderColon = false,
  columns = 1,
  uuid,
  isFlexDetailpage = false,
}: BulletedTextListProps) {
  const localHeaderClassName =
    variant === "default" ? "text-5xl mb-4 mt-3.5" : "text-2xl mb-4 mt-3.5";

  // Generate grid classes based on columns prop
  const getGridClasses = (cols: number) => {
    if (cols <= 1) return "grid grid-cols-1";
    if (cols === 2) return "grid grid-cols-1 lg:grid-cols-2";
    if (cols === 3) return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    if (cols === 4) return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4";
    if (cols >= 5)
      return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    return "grid grid-cols-1";
  };

  const gridClasses = getGridClasses(columns);

  const childrenWithProps = Children.map(children, (child) => {
    if (isValidElement<BulletedTextListItemProps>(child)) {
      return cloneElement(child, {
        icon: child.props.icon || icon || "check-circle",
        showHeaderColon: child.props.showHeaderColon ?? showHeaderColon,
      });
    }
    return child;
  });

  return (
    <div className={`mt-xl-0 col-xl-8 ${bodyClassName ? bodyClassName : ""}`}>
      {header && (
        <h2 className={cn(localHeaderClassName, headerClassName)}>{header} </h2>
      )}
      <div className="my-1">
        <ul
          className={cn(
            "list-none list-image-none list-inside",
            gridClasses,
            variant === "small" ? "!px-4" : "pl-0",
          )}
        >
          {childrenWithProps}
        </ul>
      </div>
      {description && (
        <DegreeImageRightWrapper
          section="about"
          uuid={isFlexDetailpage ? uuid : undefined}
          className=""
          useContainer={false}
        >
          <BulletedTextListDescription
            description={description}
            variant={variant}
          />
        </DegreeImageRightWrapper>
      )}
    </div>
  );
}
